/**
 * HeyGen widget controls — pills для footer (.gm__pills) + Sheet picker'ы +
 * mobile row's для MobileSettingsSheet.
 *
 * Adaptive picker'ы (AnchoredPopover на desktop / Sheet bottom на mobile) —
 * паттерн из FolderEditSheet/PresentationActionsSheet:
 *   const usePopover = isDesktop && !!anchorEl
 *
 * Экспорты:
 *   Desktop pills (рендерятся в `.gm__pills`):
 *     - HeyGenAvatarIVPills      — Fragment с pills для avatar-iv
 *     - HeyGenVideoAgentPills    — Fragment с pills для video-agent
 *     - HeyGenTranslatePills     — Fragment с pills для video-translate
 *
 *   Translate URL field (desktop + mobile, отдельным block в gm__uploads):
 *     - HeyGenTranslateUrlField  — inline text input
 *
 *   Mobile rows (рендерятся в MobileSettingsSheet):
 *     - HeyGenAvatarMobileRow    — открывает AvatarPicker как bottom Sheet
 *     - HeyGenVoiceMobileRow     — открывает VoicePicker как bottom Sheet
 */
import { useEffect, useMemo, useRef, useState } from 'react'
import Sheet from '../../../components/Sheet'
import AnchoredPopover, { useIsDesktopPopover } from '../../../components/AnchoredPopover'
import BottomBarPopover from './BottomBarPopover'
import {
	type HeyGenAvatar,
	type HeyGenVoice,
	type HeyGenLanguage,
	listHeyGenAvatars,
	listHeyGenVoices,
	listHeyGenTranslationLanguages,
} from '../../../api/heygen'
import type { GenerateSettings } from '../hooks/useGenerateSettings'

// ── Константы ──────────────────────────────────────────────
const EMOTIONS: string[] = ['Excited', 'Friendly', 'Serious', 'Soothing', 'Broadcaster']
const AVATAR_STYLES: Array<{ value: 'normal' | 'closeUp' | 'circle'; label: string }> = [
	{ value: 'normal', label: 'Обычный' },
	{ value: 'closeUp', label: 'Крупно' },
	{ value: 'circle', label: 'Круг' },
]
const SPEED_MIN = 0.5
const SPEED_MAX = 1.5
const SPEED_STEP = 0.05
const SPEED_DEFAULT = 1.0
const POPOVER_WIDTH = 440

interface PillsProps {
	settings: GenerateSettings
	updateSetting: <K extends keyof GenerateSettings>(key: K, value: GenerateSettings[K]) => void
	disabled?: boolean
}

// ═══════════════════════════════════════════════════════════════
// AVATAR PICKER (adaptive: AnchoredPopover desktop / Sheet bottom mobile)
// ═══════════════════════════════════════════════════════════════

interface AvatarPickerProps {
	open: boolean
	onClose: () => void
	currentAvatarId: string | undefined
	onApply: (avatarId: string) => void
	allowAutoPick?: boolean
	/** Anchor для popover на desktop. Без anchor — всегда Sheet (для mobile-row'ов). */
	anchorEl?: HTMLElement | null
}

export function HeyGenAvatarPicker({
	open,
	onClose,
	currentAvatarId,
	onApply,
	allowAutoPick,
	anchorEl,
}: AvatarPickerProps) {
	const isDesktop = useIsDesktopPopover()
	const usePopover = isDesktop && !!anchorEl

	const [avatars, setAvatars] = useState<HeyGenAvatar[]>([])
	const [loading, setLoading] = useState(false)
	const [error, setError] = useState<string | null>(null)
	const [genderFilter, setGenderFilter] = useState<'' | 'male' | 'female'>('')
	const [search, setSearch] = useState('')

	// Открытие — сразу «загрузка» без прошлой ошибки: подстройка во время рендера
	// (паттерн «adjusting state during render»), а не setState в теле эффекта.
	const [prevOpen, setPrevOpen] = useState(false)
	if (prevOpen !== open) {
		setPrevOpen(open)
		if (open) {
			setLoading(true)
			setError(null)
		}
	}

	useEffect(() => {
		if (!open) return
		listHeyGenAvatars()
			.then((list) => setAvatars(list))
			.catch((e) => setError(e instanceof Error ? e.message : 'Не удалось загрузить'))
			.finally(() => setLoading(false))
	}, [open])

	const filtered = useMemo(() => {
		let list = avatars
		if (genderFilter) list = list.filter((a) => a.gender === genderFilter)
		if (search) {
			const q = search.toLowerCase()
			list = list.filter(
				(a) =>
					a.avatar_name.toLowerCase().includes(q) ||
					(a.tags || []).some((t) => t.toLowerCase().includes(q)),
			)
		}
		return list
	}, [avatars, genderFilter, search])

	const body = (
		<div className="heygen-picker__body">
			<div className="heygen-picker__filters">
				<input
					type="text"
					className="heygen-picker__search"
					placeholder="Поиск по имени или тегу…"
					value={search}
					onChange={(e) => setSearch(e.target.value)}
				/>
				<div className="heygen-picker__gender">
					<button
						type="button"
						className={`heygen-picker__gender-btn${genderFilter === '' ? ' heygen-picker__gender-btn--active' : ''}`}
						onClick={() => setGenderFilter('')}
					>
						Все
					</button>
					<button
						type="button"
						className={`heygen-picker__gender-btn${genderFilter === 'female' ? ' heygen-picker__gender-btn--active' : ''}`}
						onClick={() => setGenderFilter('female')}
					>
						Жен.
					</button>
					<button
						type="button"
						className={`heygen-picker__gender-btn${genderFilter === 'male' ? ' heygen-picker__gender-btn--active' : ''}`}
						onClick={() => setGenderFilter('male')}
					>
						Муж.
					</button>
				</div>
			</div>

			{allowAutoPick && (
				<button
					type="button"
					className={`heygen-avatar-card heygen-avatar-card--auto${!currentAvatarId ? ' heygen-avatar-card--selected' : ''}`}
					onClick={() => {
						onApply('')
						onClose()
					}}
				>
					<div className="heygen-avatar-card__placeholder">✨</div>
					<div className="heygen-avatar-card__info">
						<div className="heygen-avatar-card__name">Авто-выбор</div>
						<div className="heygen-avatar-card__meta">AI подберёт аватар HeyGen</div>
					</div>
				</button>
			)}

			{loading && <div className="heygen-picker__status">Загружаем аватары HeyGen…</div>}
			{error && <div className="heygen-picker__error">{error}</div>}

			{!loading && !error && filtered.length === 0 && (
				<div className="heygen-picker__status">Ничего не найдено</div>
			)}

			<div className="heygen-avatar-grid">
				{filtered.map((a) => {
					const isSelected = a.avatar_id === currentAvatarId
					const initials = a.avatar_name.slice(0, 2).toUpperCase()
					return (
						<button
							key={a.avatar_id}
							type="button"
							className={`heygen-avatar-card${isSelected ? ' heygen-avatar-card--selected' : ''}`}
							onClick={() => {
								onApply(a.avatar_id)
								onClose()
							}}
							title={a.avatar_id}
						>
							<div className="heygen-avatar-card__cover">
								{a.preview_video_url ? (
									<video
										src={a.preview_video_url}
										poster={a.preview_image_url || undefined}
										muted
										loop
										playsInline
										onMouseEnter={(e) =>
											(e.currentTarget as HTMLVideoElement).play().catch(() => {})
										}
										onMouseLeave={(e) =>
											(e.currentTarget as HTMLVideoElement).pause()
										}
									/>
								) : a.preview_image_url ? (
									<img src={a.preview_image_url} alt="" loading="lazy" />
								) : (
									<div className="heygen-avatar-card__placeholder">{initials}</div>
								)}
								{a.premium && <span className="heygen-avatar-card__badge">Pro</span>}
							</div>
							<div className="heygen-avatar-card__info">
								<div className="heygen-avatar-card__name">{a.avatar_name}</div>
								<div className="heygen-avatar-card__meta">
									{a.gender === 'female' ? '♀' : a.gender === 'male' ? '♂' : '·'}{' '}
									{(a.tags || []).slice(0, 2).join(' · ')}
								</div>
							</div>
						</button>
					)
				})}
			</div>
		</div>
	)

	if (usePopover && anchorEl) {
		return (
			<AnchoredPopover
				open={open}
				onClose={onClose}
				anchorEl={anchorEl}
				width={POPOVER_WIDTH}
				align="center"
				className="heygen-picker heygen-picker--popover"
			>
				{body}
			</AnchoredPopover>
		)
	}

	return (
		<Sheet
			open={open}
			onClose={onClose}
			variant="bottom"
			title="Выбор аватара HeyGen"
			showHandle
			className="heygen-picker"
		>
			{body}
		</Sheet>
	)
}

// ═══════════════════════════════════════════════════════════════
// VOICE PICKER (adaptive)
// ═══════════════════════════════════════════════════════════════

interface VoicePickerProps {
	open: boolean
	onClose: () => void
	currentVoiceId: string | undefined
	onApply: (voiceId: string) => void
	allowAutoPick?: boolean
	anchorEl?: HTMLElement | null
}

export function HeyGenVoicePicker({
	open,
	onClose,
	currentVoiceId,
	onApply,
	allowAutoPick,
	anchorEl,
}: VoicePickerProps) {
	const isDesktop = useIsDesktopPopover()
	const usePopover = isDesktop && !!anchorEl

	const [voices, setVoices] = useState<HeyGenVoice[]>([])
	const [loading, setLoading] = useState(false)
	const [error, setError] = useState<string | null>(null)
	const [language, setLanguage] = useState('')
	const [genderFilter, setGenderFilter] = useState<'' | 'male' | 'female'>('')
	const [emotionOnly, setEmotionOnly] = useState(false)
	const audioRef = useRef<HTMLAudioElement | null>(null)
	const [playingId, setPlayingId] = useState<string | null>(null)

	// Открытие — «загрузка» без прошлой ошибки, закрытие — ничего не играет: подстройка
	// во время рендера, а не setState в теле эффекта. Сам звук глушит эффект ниже.
	const [prevOpen, setPrevOpen] = useState(false)
	if (prevOpen !== open) {
		setPrevOpen(open)
		if (open) {
			setLoading(true)
			setError(null)
		} else {
			setPlayingId(null)
		}
	}

	useEffect(() => {
		if (!open) return
		listHeyGenVoices()
			.then((list) => setVoices(list))
			.catch((e) => setError(e instanceof Error ? e.message : 'Не удалось загрузить'))
			.finally(() => setLoading(false))
	}, [open])

	useEffect(() => {
		if (!open) {
			audioRef.current?.pause()
			audioRef.current = null
		}
	}, [open])

	const filtered = useMemo(() => {
		let list = voices
		if (language) {
			const q = language.toLowerCase()
			list = list.filter(
				(v) =>
					(v.language || '').toLowerCase().includes(q) ||
					(v.locale || '').toLowerCase().includes(q),
			)
		}
		if (genderFilter) list = list.filter((v) => v.gender === genderFilter)
		if (emotionOnly) list = list.filter((v) => v.emotion_support)
		return list
	}, [voices, language, genderFilter, emotionOnly])

	const togglePreview = (voice: HeyGenVoice) => {
		if (!voice.preview_audio) return
		if (playingId === voice.voice_id) {
			audioRef.current?.pause()
			setPlayingId(null)
			return
		}
		audioRef.current?.pause()
		audioRef.current = new Audio(voice.preview_audio)
		audioRef.current.onended = () => setPlayingId(null)
		audioRef.current.play().catch(() => setPlayingId(null))
		setPlayingId(voice.voice_id)
	}

	const body = (
		<div className="heygen-picker__body">
			<div className="heygen-picker__filters">
				<input
					type="text"
					className="heygen-picker__search"
					placeholder="Язык: English, Russian, Spanish…"
					value={language}
					onChange={(e) => setLanguage(e.target.value)}
				/>
				<div className="heygen-picker__gender">
					<button
						type="button"
						className={`heygen-picker__gender-btn${genderFilter === '' ? ' heygen-picker__gender-btn--active' : ''}`}
						onClick={() => setGenderFilter('')}
					>
						Все
					</button>
					<button
						type="button"
						className={`heygen-picker__gender-btn${genderFilter === 'female' ? ' heygen-picker__gender-btn--active' : ''}`}
						onClick={() => setGenderFilter('female')}
					>
						Жен.
					</button>
					<button
						type="button"
						className={`heygen-picker__gender-btn${genderFilter === 'male' ? ' heygen-picker__gender-btn--active' : ''}`}
						onClick={() => setGenderFilter('male')}
					>
						Муж.
					</button>
				</div>
				<label className="heygen-picker__check">
					<input
						type="checkbox"
						checked={emotionOnly}
						onChange={(e) => setEmotionOnly(e.target.checked)}
					/>
					С эмоциями
				</label>
			</div>

			{allowAutoPick && (
				<button
					type="button"
					className={`heygen-avatar-card heygen-avatar-card--auto${!currentVoiceId ? ' heygen-avatar-card--selected' : ''}`}
					onClick={() => {
						onApply('')
						onClose()
					}}
				>
					<div className="heygen-avatar-card__placeholder">✨</div>
					<div className="heygen-avatar-card__info">
						<div className="heygen-avatar-card__name">Авто-выбор</div>
						<div className="heygen-avatar-card__meta">AI подберёт голос</div>
					</div>
				</button>
			)}

			{loading && <div className="heygen-picker__status">Загружаем голоса…</div>}
			{error && <div className="heygen-picker__error">{error}</div>}

			{!loading && !error && filtered.length === 0 && (
				<div className="heygen-picker__status">Ничего не найдено</div>
			)}

			<div className="heygen-voice-list">
				{filtered.map((v) => {
					const isSelected = v.voice_id === currentVoiceId
					const isPlaying = playingId === v.voice_id
					return (
						<div
							key={v.voice_id}
							className={`heygen-voice-row${isSelected ? ' heygen-voice-row--selected' : ''}`}
						>
							{v.preview_audio && (
								<button
									type="button"
									className="heygen-voice-row__play"
									onClick={() => togglePreview(v)}
									aria-label={isPlaying ? 'Остановить' : 'Послушать'}
								>
									{isPlaying ? '⏸' : '▶'}
								</button>
							)}
							<button
								type="button"
								className="heygen-voice-row__main"
								onClick={() => {
									onApply(v.voice_id)
									onClose()
								}}
							>
								<div className="heygen-voice-row__name">
									{v.name}
									{v.emotion_support && (
										<span className="heygen-voice-row__badge">Эмоции</span>
									)}
								</div>
								<div className="heygen-voice-row__meta">
									{v.gender === 'female' ? '♀' : v.gender === 'male' ? '♂' : '·'} ·{' '}
									{v.language}
									{v.locale && v.locale !== v.language ? ` (${v.locale})` : ''}
								</div>
							</button>
							{isSelected && <span className="heygen-voice-row__check">✓</span>}
						</div>
					)
				})}
			</div>
		</div>
	)

	if (usePopover && anchorEl) {
		return (
			<AnchoredPopover
				open={open}
				onClose={onClose}
				anchorEl={anchorEl}
				width={POPOVER_WIDTH}
				align="center"
				className="heygen-picker heygen-picker--popover"
			>
				{body}
			</AnchoredPopover>
		)
	}

	return (
		<Sheet
			open={open}
			onClose={onClose}
			variant="bottom"
			title="Выбор голоса"
			showHandle
			className="heygen-picker"
		>
			{body}
		</Sheet>
	)
}

// ═══════════════════════════════════════════════════════════════
// Имя выбранного аватара / голоса по id (пилюли и мобильные строки)
// ═══════════════════════════════════════════════════════════════

const avatarNameById = (id: string) =>
	listHeyGenAvatars().then((list) => list.find((a) => a.avatar_id === id)?.avatar_name || '')
const voiceNameById = (id: string) =>
	listHeyGenVoices().then((list) => list.find((v) => v.voice_id === id)?.name || '')

/** Имя хранится вместе со своим id: пока ответ по текущему id не пришёл — имени нет.
 *  Без сброса setState в теле эффекта, и опоздавший ответ по прошлому id не подменит имя. */
function useHeyGenName(value: string | undefined, nameById: (id: string) => Promise<string>): string {
	const [named, setNamed] = useState<{ id: string; name: string } | null>(null)
	useEffect(() => {
		if (!value) return
		let alive = true
		nameById(value)
			.then((name) => {
				if (alive) setNamed({ id: value, name })
			})
			.catch(() => {
				if (alive) setNamed({ id: value, name: '' })
			})
		return () => {
			alive = false
		}
	}, [value, nameById])
	return value && named?.id === value ? named.name : ''
}

// ═══════════════════════════════════════════════════════════════
// PILL-кнопки для footer (паттерн SunoPersonaPicker)
// ═══════════════════════════════════════════════════════════════

interface AvatarPillProps {
	value: string | undefined
	onChange: (avatarId: string) => void
	allowAutoPick?: boolean
	disabled?: boolean
}

function HeyGenAvatarPill({ value, onChange, allowAutoPick, disabled }: AvatarPillProps) {
	const [open, setOpen] = useState(false)
	const name = useHeyGenName(value, avatarNameById)
	// Якорь — callback-ref в state: ref.current в рендере читать нельзя, а кнопка
	// после монтирования сразу даёт валидный anchorEl (как в PresentationPills).
	const [btnEl, setBtnEl] = useState<HTMLButtonElement | null>(null)

	const label = value ? name || 'Аватар HeyGen' : allowAutoPick ? 'Авто-выбор' : 'Аватар HeyGen'

	return (
		<>
			<button
				ref={setBtnEl}
				type="button"
				className={`bbar-pill heygen-pill${disabled ? ' bbar-pill--disabled' : ''}`}
				onClick={() => setOpen(true)}
				disabled={disabled}
				title={value ? `Аватар HeyGen: ${name || value}` : 'Выбрать аватар HeyGen'}
			>
				<span className="bbar-pill__icon" aria-hidden="true">
					👤
				</span>
				<span className="bbar-pill__label">{label}</span>
			</button>
			<HeyGenAvatarPicker
				open={open}
				onClose={() => setOpen(false)}
				currentAvatarId={value}
				onApply={onChange}
				allowAutoPick={allowAutoPick}
				anchorEl={btnEl}
			/>
		</>
	)
}

interface VoicePillProps {
	value: string | undefined
	onChange: (voiceId: string) => void
	allowAutoPick?: boolean
	disabled?: boolean
}

function HeyGenVoicePill({ value, onChange, allowAutoPick, disabled }: VoicePillProps) {
	const [open, setOpen] = useState(false)
	const name = useHeyGenName(value, voiceNameById)
	const [btnEl, setBtnEl] = useState<HTMLButtonElement | null>(null)

	const label = value ? name || 'Голос' : allowAutoPick ? 'Авто-выбор' : 'Голос'

	return (
		<>
			<button
				ref={setBtnEl}
				type="button"
				className={`bbar-pill heygen-pill${disabled ? ' bbar-pill--disabled' : ''}`}
				onClick={() => setOpen(true)}
				disabled={disabled}
				title={value ? `Голос: ${name || value}` : 'Выбрать голос'}
			>
				<span className="bbar-pill__icon" aria-hidden="true">
					🎙
				</span>
				<span className="bbar-pill__label">{label}</span>
			</button>
			<HeyGenVoicePicker
				open={open}
				onClose={() => setOpen(false)}
				currentVoiceId={value}
				onApply={onChange}
				allowAutoPick={allowAutoPick}
				anchorEl={btnEl}
			/>
		</>
	)
}

// ═══════════════════════════════════════════════════════════════
// PILLS — Fragment'ы для рендера в .gm__pills (desktop)
// ═══════════════════════════════════════════════════════════════

/** Avatar IV: Avatar + Voice + Style + Speed + Emotion + Caption */
export function HeyGenAvatarIVPills({ settings, updateSetting, disabled }: PillsProps) {
	const speed = settings.heygenVoiceSpeed ?? SPEED_DEFAULT

	return (
		<>
			<HeyGenAvatarPill
				value={settings.heygenAvatarId}
				onChange={(id) => updateSetting('heygenAvatarId', id || undefined)}
				disabled={disabled}
			/>
			<HeyGenVoicePill
				value={settings.heygenVoiceId}
				onChange={(id) => updateSetting('heygenVoiceId', id || undefined)}
				disabled={disabled}
			/>
			<BottomBarPopover
				tooltip="Обрамление аватара HeyGen (normal / closeUp / circle)"
				label={AVATAR_STYLES.find((s) => s.value === (settings.heygenAvatarStyle || 'normal'))?.label || 'Стиль'}
				options={AVATAR_STYLES.map((s) => ({ value: s.value, label: s.label }))}
				value={settings.heygenAvatarStyle || 'normal'}
				onChange={(v) => updateSetting('heygenAvatarStyle', v as 'normal' | 'closeUp' | 'circle')}
				disabled={disabled}
			/>
			<BottomBarPopover
				tooltip="Скорость TTS-голоса"
				label={`Скорость ${speed.toFixed(2)}×`}
				slider={{
					min: SPEED_MIN,
					max: SPEED_MAX,
					step: SPEED_STEP,
					value: speed,
					onChange: (v) => updateSetting('heygenVoiceSpeed', v),
					unit: '×',
				}}
				disabled={disabled}
			/>
			<BottomBarPopover
				tooltip="Эмоция голоса (применима только к voices с эмоциями)"
				label={settings.heygenVoiceEmotion || 'Без эмоции'}
				options={[
					{ value: '', label: 'Без эмоции' },
					...EMOTIONS.map((e) => ({ value: e, label: e })),
				]}
				value={settings.heygenVoiceEmotion || ''}
				onChange={(v) => updateSetting('heygenVoiceEmotion', v || undefined)}
				disabled={disabled}
			/>
			<BottomBarPopover
				tooltip="Burn-in субтитры на видео"
				label="Субтитры"
				active={!!settings.heygenCaption}
				onToggle={() => updateSetting('heygenCaption', !settings.heygenCaption)}
				disabled={disabled}
			/>
		</>
	)
}

/** Video Agent: только Avatar (auto-pick опция) */
export function HeyGenVideoAgentPills({ settings, updateSetting, disabled }: PillsProps) {
	return (
		<>
			<HeyGenAvatarPill
				value={settings.heygenAvatarId}
				onChange={(id) => updateSetting('heygenAvatarId', id || undefined)}
				allowAutoPick
				disabled={disabled}
			/>
			<HeyGenVoicePill
				value={settings.heygenVoiceId}
				onChange={(id) => updateSetting('heygenVoiceId', id || undefined)}
				allowAutoPick
				disabled={disabled}
			/>
		</>
	)
}

/** Video Translate: Language + 2 toggle */
export function HeyGenTranslatePills({ settings, updateSetting, disabled }: PillsProps) {
	const [languages, setLanguages] = useState<HeyGenLanguage[]>([])

	useEffect(() => {
		listHeyGenTranslationLanguages().then(setLanguages).catch(() => setLanguages([]))
	}, [])

	const langLabel = languages.find((l) => l.code === settings.heygenOutputLanguage)?.label
	return (
		<>
			<BottomBarPopover
				tooltip="Целевой язык перевода"
				label={langLabel || settings.heygenOutputLanguage || 'Язык'}
				options={[
					{ value: '', label: '— Не выбрано —' },
					...languages.map((l) => ({ value: l.code, label: l.label })),
				]}
				value={settings.heygenOutputLanguage || ''}
				onChange={(v) => updateSetting('heygenOutputLanguage', v || undefined)}
				disabled={disabled}
			/>
			<BottomBarPopover
				tooltip="Подгонять длительность под темп речи разных языков"
				label="Dynamic dur."
				active={!!settings.heygenEnableDynamicDuration}
				onToggle={() =>
					updateSetting('heygenEnableDynamicDuration', !settings.heygenEnableDynamicDuration)
				}
				disabled={disabled}
			/>
			<BottomBarPopover
				tooltip="Только аудио-дорожка, без lipsync"
				label="Audio only"
				active={!!settings.heygenTranslateAudioOnly}
				onToggle={() =>
					updateSetting('heygenTranslateAudioOnly', !settings.heygenTranslateAudioOnly)
				}
				disabled={disabled}
			/>
		</>
	)
}

// ═══════════════════════════════════════════════════════════════
// MOBILE ROWS — для MobileSettingsSheet
// ═══════════════════════════════════════════════════════════════

interface MobileRowProps {
	value: string | undefined
	onChange: (id: string) => void
	disabled?: boolean
	allowAutoPick?: boolean
}

/** Avatar mobile row — стилистика как у `mss-row__header` (open Sheet picker on click). */
export function HeyGenAvatarMobileRow({ value, onChange, disabled, allowAutoPick }: MobileRowProps) {
	const [open, setOpen] = useState(false)
	const name = useHeyGenName(value, avatarNameById)

	const display = value ? name || 'Выбран аватар HeyGen' : allowAutoPick ? 'Авто-выбор' : 'Не выбран'

	return (
		<>
			<div className="mss-row mss-row--select">
				<button
					type="button"
					className="mss-row__header"
					onClick={() => !disabled && setOpen(true)}
					disabled={disabled}
				>
					<div className="mss-row__label-wrap">
						<span className="mss-row__label">Аватар HeyGen</span>
					</div>
					<div className="mss-row__current">
						<span>{display}</span>
					</div>
				</button>
			</div>
			<HeyGenAvatarPicker
				open={open}
				onClose={() => setOpen(false)}
				currentAvatarId={value}
				onApply={onChange}
				allowAutoPick={allowAutoPick}
			/>
		</>
	)
}

/** Voice mobile row */
export function HeyGenVoiceMobileRow({ value, onChange, disabled, allowAutoPick }: MobileRowProps) {
	const [open, setOpen] = useState(false)
	const name = useHeyGenName(value, voiceNameById)

	const display = value ? name || 'Выбран голос' : allowAutoPick ? 'Авто-выбор' : 'Не выбран'

	return (
		<>
			<div className="mss-row mss-row--select">
				<button
					type="button"
					className="mss-row__header"
					onClick={() => !disabled && setOpen(true)}
					disabled={disabled}
				>
					<div className="mss-row__label-wrap">
						<span className="mss-row__label">Голос</span>
					</div>
					<div className="mss-row__current">
						<span>{display}</span>
					</div>
				</button>
			</div>
			<HeyGenVoicePicker
				open={open}
				onClose={() => setOpen(false)}
				currentVoiceId={value}
				onApply={onChange}
				allowAutoPick={allowAutoPick}
			/>
		</>
	)
}

// ═══════════════════════════════════════════════════════════════
// URL field для translate (desktop + mobile)
// ═══════════════════════════════════════════════════════════════

export function HeyGenTranslateUrlField({ settings, updateSetting, disabled }: PillsProps) {
	return (
		<div className="heygen-translate-url">
			<span className="heygen-translate-url__icon" aria-hidden="true">
				🔗
			</span>
			<input
				type="url"
				className="heygen-translate-url__input"
				placeholder="Или вставьте URL: YouTube / Google Drive / прямой mp4"
				value={settings.heygenVideoUrl || ''}
				onChange={(e) => updateSetting('heygenVideoUrl', e.target.value || undefined)}
				disabled={disabled}
			/>
			{settings.heygenVideoUrl && (
				<button
					type="button"
					className="heygen-translate-url__clear"
					onClick={() => updateSetting('heygenVideoUrl', undefined)}
					disabled={disabled}
					aria-label="Очистить URL"
					title="Очистить"
				>
					×
				</button>
			)}
		</div>
	)
}

// ═══════════════════════════════════════════════════════════════
// HOOK: список языков для translation (используется в MobileSettingsSheet)
// ═══════════════════════════════════════════════════════════════

// eslint-disable-next-line react-refresh/only-export-components -- хук живёт рядом с панелями HeyGen, которым он нужен
export function useHeyGenTranslationLanguages(): HeyGenLanguage[] {
	const [languages, setLanguages] = useState<HeyGenLanguage[]>([])
	useEffect(() => {
		listHeyGenTranslationLanguages().then(setLanguages).catch(() => setLanguages([]))
	}, [])
	return languages
}

// ═══════════════════════════════════════════════════════════════
// Константы для использования в MobileSettingsSheet
// ═══════════════════════════════════════════════════════════════

export const HEYGEN_EMOTIONS = EMOTIONS
export const HEYGEN_AVATAR_STYLES = AVATAR_STYLES
// eslint-disable-next-line react-refresh/only-export-components -- пределы скорости общие у пилюли и мобильной строки
export const HEYGEN_SPEED = {
	min: SPEED_MIN,
	max: SPEED_MAX,
	step: SPEED_STEP,
	default: SPEED_DEFAULT,
}
